import { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Button, Flex } from '@mantine/core';

import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { Text, TextType } from 'ui-components/components/text';
import { Input } from 'ui-components/components/input';
import { Checkbox } from 'ui-components/components/checkbox';
import { Pill } from 'ui-components/components/pill';
import type { PillProps } from 'ui-components/components/pill/pill-types';

import { getIsValidEmail } from '@shipstation/client-common/utils/email';

import Global from '../../../../../../../../store/global-selectors';
import { ModalPortal } from '../../../../../../../../components/modal-portal';
import { shareFreightModalMessages as messages } from './share-freight-modal-messages';

import style from './share-freight-modal.module.less';

type ShareFreightModalProps = {
  onClose: () => void;
  onShare?: (recipients: string[]) => void;
};

export const ShareFreightModal = ({ onClose, onShare }: ShareFreightModalProps) => {
  const intl = useIntl();
  const currentUser = useSelector(Global.selectUserCurrentUserInfo) as { email?: string } | null;
  const currentUserEmail = currentUser?.email ?? null;

  const [emailInput, setEmailInput] = useState('');
  const [recipients, setRecipients] = useState<string[]>([]);
  const [isMyEmailChecked, setIsMyEmailChecked] = useState(false);
  const [hasInputError, setHasInputError] = useState(false);

  const trimmedInput = emailInput.trim();
  const isAddEnabled = getIsValidEmail(trimmedInput);
  const isShareEnabled = recipients.length > 0 || isMyEmailChecked || isAddEnabled;

  const handleEmailChange = useCallback((value: string) => {
    setEmailInput(value);
    setHasInputError(false);
  }, []);

  const handleAdd = useCallback(() => {
    if (!getIsValidEmail(emailInput.trim())) return;
    const trimmed = emailInput.trim().toLowerCase();
    if (!recipients.includes(trimmed)) {
      setRecipients((prev) => [...prev, trimmed]);
    }
    setEmailInput('');
  }, [emailInput, recipients]);

  const handleRemoveRecipient = useCallback<NonNullable<PillProps['onRemove']>>((_label, id) => {
    setRecipients((prev) => prev.filter((r) => r !== id));
  }, []);

  const handleMyEmailChange = useCallback((checked: boolean) => {
    setIsMyEmailChecked(checked);
  }, []);

  const handleShare = useCallback(() => {
    if (trimmedInput && !getIsValidEmail(trimmedInput)) {
      setHasInputError(true);
      return;
    }

    const allRecipients = [...recipients];
    const normalizedInput = trimmedInput.toLowerCase();
    if (trimmedInput && getIsValidEmail(trimmedInput) && !allRecipients.includes(normalizedInput)) {
      allRecipients.push(normalizedInput);
    }
    const normalizedUserEmail = currentUserEmail?.toLowerCase() ?? null;
    if (isMyEmailChecked && normalizedUserEmail && !allRecipients.includes(normalizedUserEmail)) {
      allRecipients.push(normalizedUserEmail);
    }
    onShare?.(allRecipients);
    onClose();
  }, [trimmedInput, recipients, isMyEmailChecked, currentUserEmail, onShare, onClose]);

  return (
    <ModalPortal onClose={onClose}>
      <Modal className={style.modal} size='custom'>
        <ModalHeader closeModal={onClose}>
          <Text className={style.title} textType={TextType.H3} value={messages.title} />
        </ModalHeader>
        <ModalBody className={style['modal-body']}>
          <Flex className={style.body} direction='column'>
            <Text className={style.instructions} textType={TextType.Description} value={messages.instructions} />
            <Flex className={style['recipient-group']} direction='column' gap='xs'>
              <Text className={style['recipient-label']} textType={TextType.Label} value={messages.recipientLabel} />
              <Flex direction='column' gap={4}>
                <Flex align='stretch' className={style['email-input-wrapper']}>
                  <Input
                    className={style['email-input']}
                    hasError={hasInputError}
                    onChange={handleEmailChange}
                    placeholder={intl.formatMessage(messages.emailPlaceholder)}
                    type='email'
                    value={emailInput}
                  />
                  <Button className={style['add-button']} disabled={!isAddEnabled} onClick={handleAdd} variant='secondary'>
                    <FormattedMessage {...messages.addButton} />
                  </Button>
                </Flex>
                {hasInputError && (
                  <Text className={style['input-error-text']} textType={TextType.Description} value={messages.incompleteEmailError} />
                )}
              </Flex>
            </Flex>
            <div className={style['checkbox-row']}>
              <Checkbox
                checked={isMyEmailChecked}
                label={intl.formatMessage(messages.shareMyEmail, { email: currentUserEmail ?? '' })}
                onChange={handleMyEmailChange}
              />
            </div>
            {recipients.length > 0 && (
              <Flex className={style['recipients-list']} gap='xs' wrap='wrap'>
                {recipients.map((email) => (
                  <Pill id={email} key={email} label={email} onRemove={handleRemoveRecipient} />
                ))}
              </Flex>
            )}
            <div className={style.spacer} />
            <Flex className={style.actions} justify='flex-end' gap='sm'>
              <Button className={style['cancel-button']} onClick={onClose} variant='secondary'>
                <FormattedMessage {...messages.cancelButton} />
              </Button>
              <Button disabled={!isShareEnabled} onClick={handleShare} variant='primary'>
                <FormattedMessage {...messages.shareButton} />
              </Button>
            </Flex>
          </Flex>
        </ModalBody>
      </Modal>
    </ModalPortal>
  );
};
